import { useToast } from "@/hooks/use-toast";
import type { ApiResponse } from "@/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { nanoid } from "nanoid";
import { useCallback } from "react";
import type { Feedback } from "../../../schemas";
import { api } from "../api/api";

const FEEDBACK_KEY = "FEEDBACK";

export const getProjectFeedbackQueryOptions = (projectId: string) => ({
  queryKey: [FEEDBACK_KEY, projectId],
  queryFn: () => api.getProjectFeedback(projectId),
  select: (response: ApiResponse<{ feedback: Feedback[] }>) =>
    response.data?.feedback || [],
  enabled: !!projectId,
  staleTime: 1000 * 60 * 5, // Consider data fresh for 5 minutes
});

/**
 * Hook to get feedback for a specific message within a project
 */
export function useMessageFeedback(projectId: string, messageId: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const query = useQuery({
    ...getProjectFeedbackQueryOptions(projectId),
    select: (response: ApiResponse<{ feedback: Feedback[] }>) => {
      const allFeedback = response.data?.feedback || [];
      return allFeedback.find((f) => f.messageId === messageId);
    },
  });

  const reportFeedback = useCallback(
    async (score: 0 | 1, additionalUserInput?: string) => {
      const existingFeedback = query.data;
      if (existingFeedback) {
        return; // Feedback already given
      }

      toast({
        duration: 2500,
        title: "Feedback given",
        description: "Thank you for your feedback!",
      });

      try {
        await api.giveFeedback({
          id: nanoid(),
          messageId,
          projectId,
          score,
          additionalUserInput,
        });
        queryClient.invalidateQueries({
          queryKey: [FEEDBACK_KEY, projectId],
        });
      } catch (error) {
        console.error("Error recording feedback:", error);
      }
    },
    [messageId, projectId, query.data, queryClient, toast],
  );

  return {
    ...query,
    reportFeedback,
  };
}
